import {JLastDatas, OLastDatasInit} from "lib/commons/lastDatas";
import {REG} from 'lib/commons/registry';
import {ISecurityCtxPointer} from 'lib/commons/security';

type dynString<C> = (ctx: C, s: Signboard<C>) => string;
type dynBoolean<C> = (ctx: C, s: Signboard<C>) => boolean;


/**
 * Une 'enseigne' (signboard) gère les propriétés :
 * - d'affichage (label, icon, description)
 * - d'ordonnacement (group)
 * - d'accès (visible, enabled)
 * d'une entité graphique en fonction d'un contexte C.
 *
 * L'enseigne mutualise les propriétés communes des Actions (factory de button) et des Areas (factory de view, input,...).
 */
export interface ISignboard<C> {
	getId(): string

	getLabel(ctx: C): string

	getDescription(ctx: C): string

	getIcon(ctx: C): string

	/** Les groupes permettent de trier et rassembler des enseignes dans une liste. */
	getGroup(ctx: C): string

	/** Remplacement du skin du widget. */
	getSkin(ctx: C): string

	/** Surcharge du skin du widget. */
	getSkinOver(ctx: C): string

	getLastDatasKey(ctx: C): string

	isVisible(ctx: C): boolean

	isEnabled(ctx: C): boolean

	/**
	 * Contrôle si cet enseigne est visible et active.
	 * Note : le caractère instantiable n'est pas évalué car généralement déjà réalisé en amont.
	 */
	isAvailable(ctx: C): boolean

	/**
	 * Cette enseigne doit-elle être instanciée graphiquement dans ce contexte ?
	 * Correspond à un isVisible() initial **et définitif** à la création d'une toolbar, d'un menu ou d'une view.
	 * A utiliser quand les conditions d'affichage de cette enseigne dépend des propriétés invariantes de ctx.
	 */
	isInstantiable(ctx: C): boolean
}

export class Signboard<C> implements ISignboard<C> {
	protected _id: string;
	protected _label: string | dynString<C>;
	protected _description: string | dynString<C>;
	protected _icon: string | dynString<C>;
	protected _group: string | dynString<C>;
	protected _skin: string | dynString<C>;
	protected _skinOver: string | dynString<C>;
	protected _visPerms: string | string[];
	protected _enablePerms: string | string[];
	protected _visible: boolean | dynBoolean<C>;
	protected _enabled: boolean | dynBoolean<C>;
	protected _instantiable: boolean | dynBoolean<C>;
	protected _lastDatasKey: string | dynString<C>;

	constructor(id?: string) {
		this._id = id != null /* id='' possible, key d'enum... */ ? id : "?" /* this.constructor.name NON risque régression en prod si minification. */;
	}

	getId() {return this._id}

	getLabel(ctx: C) {
		return (typeof this._label === 'function') ? this._label(ctx, this) : (this._label !== undefined) ? this._label : this._id;
	}

	setLabel(label: string | dynString<C>): this {
		this._label = label;
		return this;
	}

	/*
	 * Description de l'action : exploité en tooltip lorsque celà est possible
	 */
	getDescription(ctx: C) {
		return (typeof this._description === 'function') ? this._description(ctx, this) : this._description;
	}

	setDescription(description: string | dynString<C>): this {
		this._description = description;
		return this;
	}

	getIcon(ctx: C) {
		return (typeof this._icon === 'function') ? this._icon(ctx, this) : this._icon;
	}

	setIcon(icon: string | dynString<C>): this {
		this._icon = icon;
		return this;
	}

	/** Les groupes permettent de trier et rassembler des enseignes dans une liste. */
	getGroup(ctx: C) {
		return (typeof this._group === 'function') ? this._group(ctx, this) : this._group;
	}

	setGroup(group: string | dynString<C>): this {
		this._group = group;
		return this;
	}

	/** Remplacement du skin du widget. */
	getSkin(ctx: C) {
		return (typeof this._skin === 'function') ? this._skin(ctx, this) : this._skin;
	}

	setSkin(skin: string | dynString<C>): this {
		this._skin = skin;
		return this;
	}

	/** Surcharge du skin du widget. */
	getSkinOver(ctx: C): string {
		return (typeof this._skinOver === 'function') ? this._skinOver(ctx, this) : this._skinOver;
	}

	setSkinOver(skinOver: string | dynString<C>): this {
		this._skinOver = skinOver;
		return this;
	}

	getLastDatasKey(ctx: C): string {
		return (typeof this._lastDatasKey === 'function') ? this._lastDatasKey(ctx, this) : this._lastDatasKey;
	}

	setLastDatasKey(lastDatasKey: string | dynString<C>): this {
		this._lastDatasKey = lastDatasKey;
		return this;
	}

	initLastDatas<O extends OLastDatasInit>(ctx: C, lastDatas: JLastDatas, init: O): O {
		const k = this.getLastDatasKey(ctx);
		if (k) {
			init.lastDatasKey = k;
			if (lastDatas) init.lastDatas = lastDatas[k];
		} else if (lastDatas) {
			init.lastDatas = lastDatas;
		}
		return init;
	}

	/**
	 * Ajoute une ou plusieurs permissions à l'ensemble des permissions sans lesquelles
	 * l'action ne peut s'afficher (impacte la propriété Visible).
	 * Opérateur OR : il suffit d'une seule perm autorisée pour être visible.
	 *
	 * @param perms Tableau de permissions ou permission seule à ajouter à l'ensemble
	 */
	requireVisiblePerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireVisiblePerm(perms[i]);
			} else {
				if (!this._visPerms) this._visPerms = perms;
				else {
					if (typeof this._visPerms === "string") this._visPerms = [this._visPerms];
					if (this._visPerms.indexOf(perms) == -1) this._visPerms.push(perms);
				}
			}
		}
		return this;
	}

	/**
	 * Ajoute une ou plusieurs permissions controlant l'activation de l'enseigne.
	 * Opérateur OR : il suffit d'une seule perm autorisée pour être actif.
	 *
	 * @param perms Tableau de permissions ou permission seule à ajouter à l'ensemble.
	 */
	requireEnabledPerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireEnabledPerm(perms[i]);
			} else {
				if (!this._enablePerms) this._enablePerms = perms;
				else {
					if (typeof this._enablePerms === "string") this._enablePerms = [this._enablePerms];
					if (this._enablePerms.indexOf(perms) == -1) this._enablePerms.push(perms);
				}
			}
		}
		return this;
	}

	isVisible(ctx: C): boolean {
		if (typeof this._visible === 'function' && this._visible(ctx, this) === false) return false;
		if (this._visible === false) return false;
		return this.checkObjectRootVisiblePerm(ctx);
	}

	setVisible(visible: boolean | dynBoolean<C>): this {
		this._visible = visible;
		return this;
	}


	isEnabled(ctx: C): boolean {
		if (typeof this._enabled === 'function' && this._enabled(ctx, this) === false) return false;
		if (this._enabled === false) return false;
		return this.checkObjectRootPerm(ctx);
	}

	setEnabled(enabled: boolean | dynBoolean<C>): this {
		this._enabled = enabled;
		return this;
	}

	isAvailable(ctx: C): boolean {return this.isVisible(ctx) && this.isEnabled(ctx)}

	setInstantiable(instantiable: boolean | dynBoolean<C>): this {
		this._instantiable = instantiable;
		return this;
	}

	isInstantiable(ctx: C): boolean {return true}

	/**
	 * Teste les permissions de visibilité de l'action sur l'objectRoot.
	 *
	 * @param perms [optionnel] Liste des permissions ou permission seule à tester
	 * @param and Opérateur appliqué entre les permissions. 'or' par défaut.
	 * @return true si les rôles du contexte de sécurité ont la ou les permissions
	 */
	checkObjectRootVisiblePerm(ctx: C, perms?: string | string[], and?: boolean): boolean {
		if (!perms) perms = this._visPerms;
		return perms ? REG.getReg(ctx).hasPerm(perms, (ctx as ISecurityCtxPointer).securityCtx, and) : true;
	}

	/**
	 * Teste les permissions d'activation sur l'objectRoot.
	 *
	 * @param perms [optionnel] Liste des permissions ou permission seule à tester
	 * @param and Opérateur appliqué entre les permissions. 'or' par défaut.
	 * @return true si les rôles du contexte de sécurité ont la ou les permissions
	 */
	checkObjectRootPerm(ctx: C, perms?: string | string[], and?: boolean): boolean {
		if (!perms) perms = this._enablePerms;
		return perms ? REG.getReg(ctx).hasPerm(perms, (ctx as ISecurityCtxPointer).securityCtx, and) : true;
	}

	getEnablePerms(): string | string[] | null {return this._enablePerms}

	getVisiblePerms(): string | string[] | null {return this._visPerms}

	toString() {return this._id}
}

/**
 * Méthode implémentable par un contexte.
 * Permet à un contexte de précalculer / geler des états pendant un cycle de refresh (d'actions ou d'areas) multiples.
 * Peut éviter des recaculs de styles et des reflow de layout importants, notamment pour le redraw d'actions
 * dépendant d'une Selection.
 */
export interface IRefreshHook {
	/** Au cycle before peut retourner 'stop' pour abndonner le refresh (détection race conditions).*/
	onRefreshCycle(before: boolean): 'stop' | void
}

export function isRefreshHook(c: any): c is IRefreshHook {return c && ('onRefreshCycle' in c)}
